/**
 * ============================================================================
 * TuTaxi Madrid Norte - Landing Page Principal
 * ============================================================================
 * 
 * Landing page optimizada para SEO local en Madrid Norte.
 * Zonas de cobertura: Mirasierra, Barrio del Pilar, Fuencarral.
 * 
 * CARACTERÍSTICAS:
 * - Mobile First (90% del tráfico es móvil)
 * - SEO optimizado con keywords geolocalizados
 * - Sistema de reserva vía WhatsApp
 * - Tracking de conversiones con Google Analytics
 * 
 * CONTACTO:
 * - Teléfono: 639759160
 * - WhatsApp: 34639759160
 * - Propietario: Jose Antonio Villacorta
 * 
 * @author TuTaxi Madrid Norte
 * @version 1.0.0
 * @license Todos los derechos reservados
 */

import "@/App.css";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { 
  Phone, 
  MapPin, 
  ShieldCheck, 
  Clock, 
  Euro, 
  Leaf, 
  Plane, 
  ChevronDown,
  Menu,
  X,
  Star,
  Route,
  BadgeCheck,
  Navigation
} from "lucide-react";

// ============================================================================
// ICONO OFICIAL DE WHATSAPP (SVG)
// ============================================================================

/**
 * Componente SVG del logo oficial de WhatsApp
 * Usado en todos los botones de WhatsApp para reconocimiento instantáneo
 */
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ============================================================================
// IMAGEN DEL TAXI REAL
// ============================================================================

/**
 * URL de la imagen real del taxi Toyota Prius+ híbrido
 */
const TAXI_IMAGE_URL = "https://customer-assets.emergentagent.com/job_reserva-rapida-taxi/artifacts/v55d380q_20250731_1421_Taxi%2Ben%2BPlaza%2BSoleada_remix_01k1g6nsnafxn8yx85mb0jyw40-1200w.webp";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent } from "./components/ui/card";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// ============================================================================
// CONFIGURACIÓN GLOBAL
// ============================================================================

/**
 * Número de teléfono para llamadas (sin prefijo internacional)
 */
const PHONE_NUMBER = "639759160";

/**
 * Número de WhatsApp (con prefijo internacional sin +)
 */
const WHATSAPP_NUMBER = "34639759160";

/**
 * Mensaje predeterminado para WhatsApp (codificado para URL)
 */
const WHATSAPP_DEFAULT_MESSAGE = encodeURIComponent("Hola, quiero reservar un Taxi.");

/**
 * Datos de las zonas de cobertura para SEO
 * Cada zona incluye las calles principales para indexación por proximidad
 */
const zones = [
  {
    id: "mirasierra",
    name: "Mirasierra",
    streets: ["Calle Costa Brava", "Av. Cardenal Herrera Oria"],
    icon: MapPin,
  },
  {
    id: "fuencarral",
    name: "Barrio de Fuencarral",
    streets: ["C/ Nuestra Señora de Valverde", "Av. Llano Castellano"],
    icon: Navigation,
  },
  {
    id: "pilar",
    name: "Barrio del Pilar",
    streets: ["Av. Monforte de Lemos", "C/ Fermín Caballero", "C/ Melchor Fernández Almagro"],
    icon: Route,
  },
];

// ============================================================================
// UTILIDADES DE TRACKING (Google Analytics)
// ============================================================================

/**
 * Envía un evento a Google Analytics 4
 * 
 * @param {string} eventName - Nombre del evento (ej: 'click_call', 'click_whatsapp')
 * @param {Object} eventParams - Parámetros adicionales del evento
 * 
 * @example
 * trackEvent('click_call', { location: 'header', phone: '639759160' });
 */
const trackEvent = (eventName, eventParams = {}) => {
  // Verificar que gtag esté disponible (Google Analytics cargado)
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams);
  }
  
  // También enviar a dataLayer para GTM
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      event: eventName,
      ...eventParams
    });
  }
};

/**
 * Trackea un clic en botón de llamada telefónica
 * 
 * @param {string} location - Ubicación del botón (header, hero, footer, etc.)
 */
const trackCallClick = (location) => {
  trackEvent('click_call', {
    event_category: 'engagement',
    event_label: location,
    phone_number: PHONE_NUMBER,
    value: 1
  });
};

/**
 * Trackea un clic en botón de WhatsApp
 * 
 * @param {string} location - Ubicación del botón
 * @param {string} [messageType] - Tipo de mensaje (reserva, aeropuerto, presupuesto)
 */
const trackWhatsAppClick = (location, messageType = 'general') => {
  trackEvent('click_whatsapp', {
    event_category: 'engagement',
    event_label: location,
    message_type: messageType,
    value: 1
  });
};

/**
 * Trackea el envío del formulario de reserva
 * 
 * @param {Object} formData - Datos del formulario
 */
const trackFormSubmit = (formData) => {
  trackEvent('form_submit', {
    event_category: 'conversion',
    event_label: 'booking_form',
    vehicle_type: formData.vehiculo || 'estandar',
    value: 1
  });
};

// ============================================================================
// COMPONENTE: Header (Navegación Principal)
// ============================================================================

/**
 * Header sticky con navegación y botones de acción
 * 
 * Características:
 * - Se vuelve opaco al hacer scroll (efecto blur)
 * - Menú responsive (hamburguesa en móvil)
 * - Botones de WhatsApp y Llamar siempre visibles
 * - Navegación con smooth scroll a secciones
 */
const Header = () => {
  // Estado para detectar scroll y cambiar estilos del header
  const [isScrolled, setIsScrolled] = useState(false);
  // Estado para controlar el menú móvil
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Listener de scroll para efecto sticky
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /**
   * Navega suavemente a una sección de la página
   * @param {string} sectionId - ID del elemento HTML destino
   */
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white shadow-md" 
          : "bg-white/90 backdrop-blur-sm shadow-sm"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo - Clic vuelve al inicio */}
          <a 
            href="#hero" 
            onClick={(e) => { e.preventDefault(); scrollToSection("hero"); }}
            className="font-heading text-xl md:text-2xl font-black uppercase tracking-tighter text-[#1A1A1A]"
            data-testid="logo"
          >
            TuTaxi<span className="text-[#F5D109]">Madrid</span>Norte
          </a>

          {/* Navegación Desktop - Oculta en móvil */}
          <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
            <button 
              onClick={() => scrollToSection("servicios")}
              className="text-sm font-semibold text-[#1A1A1A] hover:text-[#F5D109] transition-colors"
            >
              Servicios
            </button>
            <button 
              onClick={() => scrollToSection("zonas")}
              className="text-sm font-semibold text-[#1A1A1A] hover:text-[#F5D109] transition-colors"
            >
              Zonas
            </button>
            <button 
              onClick={() => scrollToSection("tarifas")}
              className="text-sm font-semibold text-[#1A1A1A] hover:text-[#F5D109] transition-colors"
            >
              Tarifas
            </button>
            <button 
              onClick={() => scrollToSection("reserva")}
              className="text-sm font-semibold text-[#1A1A1A] hover:text-[#F5D109] transition-colors"
            >
              Reservar
            </button>
          </nav>

          {/* Botones CTA Desktop */}
          <div className="hidden md:flex items-center gap-3">
            {/* Botón WhatsApp */}
            <a 
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_DEFAULT_MESSAGE}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              onClick={() => trackWhatsAppClick('header')}
              data-testid="header-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-4 py-2 rounded-full text-sm hover:bg-[#20BD5A] transition-colors"
            >
              <WhatsAppIcon className="w-4 h-4" />
              WhatsApp
            </a>
            {/* Botón Llamar - Color amarillo taxi */}
            <a 
              href={`tel:+34${PHONE_NUMBER}`}
              rel="nofollow"
              onClick={() => trackCallClick('header')}
              data-testid="header-call-btn"
              className="inline-flex items-center gap-2 bg-[#F5D109] text-black font-bold px-5 py-2 rounded-full text-sm hover:bg-[#E5C200] transition-colors yellow-shadow"
            >
              <Phone className="w-4 h-4" />
              Llamar Ahora
            </a>
          </div>

          {/* Botón Menú Móvil */}
          <button 
            className="md:hidden p-2 text-[#1A1A1A]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Menú Móvil Desplegable */}
        {mobileMenuOpen && (
          <div 
            className="md:hidden bg-white border-t border-black/5 py-4 animate-fade-in"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-3">
              <button 
                onClick={() => scrollToSection("servicios")}
                className="text-left px-4 py-2 text-[#1A1A1A] font-medium hover:bg-gray-50 rounded-lg"
              >
                Servicios
              </button>
              <button 
                onClick={() => scrollToSection("zonas")}
                className="text-left px-4 py-2 text-[#1A1A1A] font-medium hover:bg-gray-50 rounded-lg"
              >
                Zonas
              </button>
              <button 
                onClick={() => scrollToSection("tarifas")}
                className="text-left px-4 py-2 text-[#1A1A1A] font-medium hover:bg-gray-50 rounded-lg"
              >
                Tarifas
              </button>
              <button 
                onClick={() => scrollToSection("reserva")}
                className="text-left px-4 py-2 text-[#1A1A1A] font-medium hover:bg-gray-50 rounded-lg"
              >
                Reservar
              </button>
              {/* CTAs Móvil - Botones grandes para fácil pulsación con el pulgar */}
              <div className="flex flex-col gap-2 px-4 pt-2">
                <a 
                  href={`tel:+34${PHONE_NUMBER}`}
                  rel="nofollow"
                  onClick={() => trackCallClick('mobile_menu')}
                  className="flex items-center justify-center gap-2 bg-[#F5D109] text-black font-bold py-4 rounded-full text-base hover:bg-[#E5C200] transition-colors yellow-shadow mobile-cta"
                  data-testid="mobile-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Llamar al 639 759 160
                </a>
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_DEFAULT_MESSAGE}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  onClick={() => trackWhatsAppClick('mobile_menu')}
                  className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-4 rounded-full text-base hover:bg-[#20BD5A] transition-colors mobile-cta"
                  data-testid="mobile-whatsapp-btn"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  WhatsApp
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// ============================================================================
// COMPONENTE: Hero Section (Sección Principal)
// ============================================================================

/**
 * Sección hero con imagen de fondo y formulario de reserva
 * 
 * Características:
 * - Imagen de fondo con gradiente para legibilidad
 * - H1 con keywords SEO principales
 * - Badges de confianza (Licencia, 24h, Precios Oficiales)
 * - Formulario de reserva flotante en desktop
 * - CTAs grandes en móvil
 */
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      className="relative min-h-[100svh] flex items-center md:items-end pb-6 md:pb-20 pt-20 md:pt-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Imagen de Fondo con alt text SEO - TAXI REAL */}
      <div className="absolute inset-0 z-0">
        <img 
          src={TAXI_IMAGE_URL}
          alt="Toyota Prius+ Híbrido Taxi oficial Madrid Norte - TuTaxi Mirasierra"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        {/* Overlay suave - solo para contraste del texto */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20 md:to-transparent"></div>
        {/* Gradiente desde abajo para contenido */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-6xl relative z-10">
        <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center md:items-end">
          {/* Contenido de Texto */}
          <div className="space-y-4 md:space-y-5 animate-fade-in-up">
            {/* Badge - Vehículo Oficial */}
            <div className="inline-flex items-center gap-2 bg-[#F5D109] px-3 py-1.5 md:px-4 md:py-2 rounded-full shadow-lg">
              <ShieldCheck className="w-4 h-4 text-black" />
              <span className="text-xs md:text-sm font-bold text-black">Vehículo Oficial · Hasta 4 Pasajeros</span>
            </div>
            
            {/* H1 Principal - Keywords SEO */}
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold uppercase tracking-tighter leading-[1.1] text-white" style={{textShadow: '2px 2px 8px rgba(0,0,0,0.8)'}}>
              Reserva tu Taxi en Mirasierra, Barrio del Pilar y Fuencarral
            </h1>
            
            {/* Subtítulo con propuesta de valor */}
            <p className="text-base md:text-lg text-white leading-relaxed max-w-lg" style={{textShadow: '1px 1px 4px rgba(0,0,0,0.7)'}}>
              <strong className="text-[#F5D109]">TuTaxi Madrid Norte</strong> – Servicio exclusivo en vehículo oficial. 
              Reserva anticipada (mínimo 2-3 horas). Puntualidad garantizada.
            </p>

            {/* Badges de Confianza - Más compactos en móvil */}
            <div className="flex flex-wrap gap-2 md:gap-3">
              <div className="flex items-center gap-1.5 text-xs md:text-sm text-white bg-white/20 backdrop-blur-sm px-2.5 py-1 md:px-3 md:py-1.5 rounded-full border border-white/30">
                <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#F5D109]" />
                <span>Licencia Oficial</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs md:text-sm text-white bg-white/20 backdrop-blur-sm px-2.5 py-1 md:px-3 md:py-1.5 rounded-full border border-white/30">
                <Euro className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#F5D109]" />
                <span>Tarifas Oficiales</span>
              </div>
            </div>

            {/* CTAs Móvil - Solo visible en pantallas pequeñas - GRANDES Y ACCESIBLES */}
            <div className="flex flex-col gap-3 pt-2 md:hidden">
              <a 
                href={`tel:+34${PHONE_NUMBER}`}
                rel="nofollow"
                onClick={() => trackCallClick('hero_mobile')}
                className="flex items-center justify-center gap-3 bg-[#F5D109] text-black font-bold py-5 px-6 rounded-2xl text-xl shadow-xl active:scale-95 transition-transform"
                data-testid="hero-mobile-call-btn"
              >
                <Phone className="w-7 h-7" />
                Llamar al 639 759 160
              </a>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_DEFAULT_MESSAGE}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={() => trackWhatsAppClick('hero_mobile')}
                className="flex items-center justify-center gap-3 bg-[#25D366] text-white font-bold py-5 px-6 rounded-2xl text-xl shadow-xl active:scale-95 transition-transform"
                data-testid="hero-mobile-whatsapp-btn"
              >
                <WhatsAppIcon className="w-7 h-7" />
                WhatsApp
              </a>
            </div>
          </div>

          {/* Formulario de Reserva - Solo visible en desktop */}
          <div className="hidden md:block animate-fade-in-up delay-200">
            <BookingForm />
          </div>
        </div>
      </div>

      {/* Indicador de Scroll */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <ChevronDown className="w-6 h-6 text-white/80" />
      </div>
    </section>
  );
};

// ============================================================================
// COMPONENTE: Formulario de Reserva
// ============================================================================

/**
 * Formulario de reserva que envía datos a WhatsApp
 * 
 * Campos:
 * - Origen: Dirección de recogida
 * - Destino: Dirección de destino
 * - Fecha: Fecha del servicio (mínimo hoy)
 * - Hora: Hora del servicio
 * - Tipo de Vehículo: Estándar, ECO, CERO, Monovolumen
 * - Teléfono: Número de contacto del cliente
 * 
 * Al enviar, abre WhatsApp con mensaje pre-formateado
 */
const BookingForm = () => {
  // Estado del formulario (simplificado - un solo vehículo de 4 plazas)
  const [formData, setFormData] = useState({
    origen: "",
    destino: "",
    fecha: "",
    hora: "",
    telefono: "",
  });

  /**
   * Maneja cambios en inputs de texto
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Envía el formulario a WhatsApp
   * Valida campos obligatorios y construye mensaje pre-formateado
   */
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    // Validación de campos obligatorios
    if (!formData.origen || !formData.destino || !formData.fecha || !formData.hora || !formData.telefono) {
      toast.error("Por favor, completa todos los campos obligatorios");
      return;
    }

    // Trackear conversión
    trackFormSubmit(formData);

    // Construir mensaje para WhatsApp
    const message = `Hola, quiero reservar un TuTaxi.

Origen: ${formData.origen}
Destino: ${formData.destino}
Fecha: ${formData.fecha}
Hora: ${formData.hora}
Teléfono: ${formData.telefono}`;

    // Codificar mensaje para URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    
    // Abrir WhatsApp en nueva pestaña
    window.open(whatsappUrl, "_blank");
    toast.success("Redirigiendo a WhatsApp...");
  }, [formData]);

  // Fecha mínima: hoy
  const today = new Date().toISOString().split('T')[0];

  return (
    <Card className="bg-white p-6 md:p-8 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-black/5 max-w-md w-full" data-testid="booking-form-card">
      <CardContent className="p-0">
        {/* Título del formulario */}
        <div className="mb-6">
          <h3 className="font-heading text-2xl font-bold uppercase text-[#1A1A1A]">Reserva tu Taxi</h3>
          <p className="text-sm text-[#666666] mt-1">Vehículo oficial · Hasta 4 pasajeros</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
          {/* Campo: Origen */}
          <div>
            <Label htmlFor="origen" className="text-sm font-semibold text-[#1A1A1A]">Origen *</Label>
            <Input 
              id="origen"
              name="origen"
              placeholder="Ej: Calle Costa Brava, Mirasierra"
              value={formData.origen}
              onChange={handleChange}
              className="mt-1 bg-gray-50 border-gray-200 focus:border-[#F5D109] focus:ring-[#F5D109]/20 rounded-lg h-12"
              data-testid="input-origen"
            />
          </div>
          
          {/* Campo: Destino */}
          <div>
            <Label htmlFor="destino" className="text-sm font-semibold text-[#1A1A1A]">Destino *</Label>
            <Input 
              id="destino"
              name="destino"
              placeholder="Ej: Aeropuerto T4"
              value={formData.destino}
              onChange={handleChange}
              className="mt-1 bg-gray-50 border-gray-200 focus:border-[#F5D109] focus:ring-[#F5D109]/20 rounded-lg h-12"
              data-testid="input-destino"
            />
          </div>
          
          {/* Campos: Fecha y Hora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fecha" className="text-sm font-semibold text-[#1A1A1A]">Fecha *</Label>
              <Input 
                id="fecha"
                name="fecha"
                type="date"
                min={today}
                value={formData.fecha}
                onChange={handleChange}
                className="mt-1 bg-gray-50 border-gray-200 focus:border-[#F5D109] focus:ring-[#F5D109]/20 rounded-lg h-12"
                data-testid="input-fecha"
              />
            </div>
            <div>
              <Label htmlFor="hora" className="text-sm font-semibold text-[#1A1A1A]">Hora *</Label>
              <Input 
                id="hora"
                name="hora"
                type="time"
                value={formData.hora}
                onChange={handleChange}
                className="mt-1 bg-gray-50 border-gray-200 focus:border-[#F5D109] focus:ring-[#F5D109]/20 rounded-lg h-12"
                data-testid="input-hora"
              />
            </div>
          </div>
          
          {/* Campo: Teléfono */}
          <div>
            <Label htmlFor="telefono" className="text-sm font-semibold text-[#1A1A1A]">Teléfono de Contacto *</Label>
            <Input 
              id="telefono"
              name="telefono"
              type="tel"
              placeholder="Tu número de teléfono"
              value={formData.telefono}
              onChange={handleChange}
              className="mt-1 bg-gray-50 border-gray-200 focus:border-[#F5D109] focus:ring-[#F5D109]/20 rounded-lg h-12"
              data-testid="input-telefono"
            />
          </div>
          
          {/* Nota sobre tarifas */}
          <p className="text-xs text-[#666666] text-center">
            Tarifas oficiales reguladas por taxímetro
          </p>
          
          {/* Botón de Envío */}
          <Button 
            type="submit"
            className="w-full bg-[#F5D109] text-black font-bold py-4 h-14 rounded-full text-lg hover:bg-[#E5C200] transition-colors yellow-shadow uppercase tracking-wide"
            data-testid="submit-booking-btn"
          >
            <WhatsAppIcon className="w-5 h-5 mr-2" />
            Enviar por WhatsApp
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// COMPONENTE: Sección de Servicios
// ============================================================================

/**
 * Muestra los servicios principales en tarjetas
 * H2 optimizado para SEO con keywords de zona
 */
const ServicesSection = () => {
  // Datos de servicios
  const services = [
    {
      icon: Clock,
      title: "Reserva Anticipada",
      description: "Reserva con 2-3 horas de antelación para garantizar tu taxi. Puntualidad asegurada.",
    },
    {
      icon: Leaf,
      title: "Flota ECO y CERO",
      description: "Vehículos híbridos y 100% eléctricos. Comprometidos con el medio ambiente.",
    },
    {
      icon: ShieldCheck,
      title: "Licencia Oficial",
      description: "Taxi con licencia oficial del Ayuntamiento de Madrid. Taxímetro homologado.",
    },
    {
      icon: Star,
      title: "Servicio Premium",
      description: "Atención personalizada, vehículos limpios y conductor profesional.",
    },
  ];

  return (
    <section id="servicios" className="py-20 md:py-32 bg-[#F9F9F9]" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Título H2 con keywords SEO */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#1A1A1A]">
            Servicio de Taxi en Calle Costa Brava y Mirasierra
          </h2>
          <p className="text-lg text-[#666666] mt-4 max-w-2xl mx-auto">
            Tu taxi de confianza en Madrid Norte con la mejor calidad de servicio
          </p>
        </div>

        {/* Grid de tarjetas de servicios */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <Card 
              key={index}
              className="bg-white border border-black/5 shadow-sm p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 rounded-xl card-hover"
              data-testid={`service-card-${index}`}
            >
              <CardContent className="p-0">
                <div className="w-12 h-12 bg-[#F5D109]/10 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-[#F5D109]" />
                </div>
                <h3 className="font-heading text-xl font-bold uppercase text-[#1A1A1A] mb-2">{service.title}</h3>
                <p className="text-[#666666] text-sm leading-relaxed">{service.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// COMPONENTE: Sección de Zonas de Cobertura (SEO CRÍTICO)
// ============================================================================

/**
 * Sección de zonas de cobertura - CRÍTICA PARA SEO
 * 
 * Muestra tarjetas interactivas con los barrios y calles específicas
 * para indexación por proximidad en Google.
 * 
 * Keywords incluidos:
 * - Mirasierra, Barrio del Pilar, Fuencarral
 * - Calle Costa Brava, Av. Cardenal Herrera Oria
 * - C/ Nuestra Señora de Valverde, Av. Llano Castellano
 * - Av. Monforte de Lemos, C/ Fermín Caballero, C/ Melchor Fernández Almagro
 */
const ZonesSection = () => {
  return (
    <section id="zonas" className="py-20 md:py-32 bg-white" data-testid="zones-section">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Título H2 con keywords SEO */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#1A1A1A]">
            Tu Taxi Oficial en el Barrio del Pilar y Fuencarral
          </h2>
          <p className="text-lg text-[#666666] mt-4 max-w-2xl mx-auto">
            Cobertura completa en Madrid Norte. Recogemos en las principales calles y avenidas de tu barrio.
          </p>
        </div>

        {/* Tarjetas de Zonas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {zones.map((zone) => (
            <Card 
              key={zone.id}
              className="bg-white border-2 border-black/5 shadow-sm hover:border-[#F5D109]/50 hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden card-hover"
              data-testid={`zone-card-${zone.id}`}
            >
              <CardContent className="p-6 md:p-8">
                {/* Icono y nombre de zona */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#F5D109] rounded-xl flex items-center justify-center">
                    <zone.icon className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase text-[#1A1A1A]">{zone.name}</h3>
                </div>
                
                <p className="text-[#666666] mb-4 text-sm">
                  Servicio de taxi con recogida rápida en todas las calles del barrio.
                </p>
                
                {/* Lista de calles (SEO) */}
                <div className="border-t border-black/5 pt-4">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-[#666666] mb-3">
                    Principales Calles
                  </h4>
                  <ul className="street-list space-y-2">
                    {zone.streets.map((street, idx) => (
                      <li key={idx} className="text-[#1A1A1A] font-medium">{street}</li>
                    ))}
                  </ul>
                </div>
                
                {/* Botón de llamada para esta zona */}
                <a 
                  href={`tel:+34${PHONE_NUMBER}`}
                  rel="nofollow"
                  onClick={() => trackCallClick(`zone_${zone.id}`)}
                  className="mt-6 flex items-center justify-center gap-2 bg-[#1A1A1A] text-white font-semibold py-3 px-4 rounded-full text-sm hover:bg-[#F5D109] hover:text-black transition-colors w-full"
                  data-testid={`zone-call-btn-${zone.id}`}
                >
                  <Phone className="w-4 h-4" />
                  Pedir Taxi en {zone.name}
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Texto adicional para SEO (keyword stuffing natural) */}
        <div className="mt-12 md:mt-16 bg-[#F9F9F9] p-6 md:p-8 rounded-2xl">
          <h3 className="font-heading text-xl md:text-2xl font-bold uppercase text-[#1A1A1A] mb-4">
            Cobertura en todas las zonas de Madrid
          </h3>
          <p className="text-[#666666] leading-relaxed">
            <strong>TuTaxi Madrid Norte</strong> ofrece servicio de taxi en todas las zonas de Madrid.
            <b>Realizamos recogidas en <strong>otros barrios residenciales </strong> como,<strong> Barrio Salamanca</strong>, 
            <strong>Chamberi</strong>, <strong>El Viso</strong> y, 
            <strong>Zona centro</strong>.
            </b> 
             Reserva con antelación y disfruta de un servicio premium 
            con vehículos ECO y CERO EMISIONES.
          </p>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// COMPONENTE: Sección Tarifa Aeropuerto
// ============================================================================

/**
 * Banner destacado con tarifa fija al aeropuerto
 * Diseño oscuro para contraste y destacar el precio
 */
const AirportSection = () => {
  return (
    <section id="tarifas" className="py-20 md:py-32 bg-[#1A1A1A] relative overflow-hidden" data-testid="airport-section">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F5D109] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F5D109] rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 max-w-6xl relative z-10">
        <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#F5D109]/20 px-4 py-2 rounded-full mb-6">
              <Plane className="w-5 h-5 text-[#F5D109]" />
              <span className="text-sm font-semibold text-white uppercase tracking-wide">Aeropuerto e IFEMA</span>
            </div>
            
            {/* Título H2 */}
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
              Tarifas Oficiales del Ayuntamiento de Madrid
            </h2>
            
            <p className="text-lg text-white/70 mb-6 leading-relaxed">
              Traslado directo desde Mirasierra, Barrio del Pilar o Fuencarral al Aeropuerto Madrid-Barajas. 
              Servicio exclusivo en vehículo oficial de hasta 4 pasajeros.
            </p>

            {/* Nota legal */}
            <p className="text-sm text-white/50 mb-8 leading-relaxed border-l-2 border-[#F5D109]/50 pl-4">
              Precios regulados por taxímetro. Consulte suplementos oficiales para trayectos al Aeropuerto e IFEMA.
            </p>
            
            {/* Botones CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`tel:+34${PHONE_NUMBER}`}
                rel="nofollow"
                onClick={() => trackCallClick('airport')}
                className="flex items-center justify-center gap-2 bg-[#F5D109] text-black font-bold py-4 px-8 rounded-full text-lg hover:bg-[#E5C200] transition-colors yellow-shadow"
                data-testid="airport-call-btn"
              >
                <Phone className="w-5 h-5" />
                Reservar Aeropuerto
              </a>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hola, quiero reservar un taxi al aeropuerto.")}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={() => trackWhatsAppClick('airport', 'aeropuerto')}
                className="flex items-center justify-center gap-2 bg-white/10 text-white font-semibold py-4 px-8 rounded-full text-lg hover:bg-white/20 transition-colors border border-white/20"
                data-testid="airport-whatsapp-btn"
              >
                <WhatsAppIcon className="w-5 h-5" />
                WhatsApp
              </a>
            </div>
          </div>
          
          {/* Tarifa oficial destacada */}
          <div className="text-center md:text-right">
            <div className="inline-block bg-[#F5D109]/10 border-2 border-[#F5D109]/30 p-8 md:p-12 rounded-3xl">
              <div className="w-16 h-16 bg-[#F5D109] rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-black" />
              </div>
              <span className="block text-white font-heading text-2xl md:text-3xl font-bold uppercase mb-2">Tarifa Oficial</span>
              <span className="block text-white/70 text-sm font-medium">Regulada por el</span>
              <span className="block text-[#F5D109] text-lg font-semibold">Ayuntamiento de Madrid</span>
              <span className="block text-white/50 text-xs mt-4">Taxímetro homologado</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// COMPONENTE: Sección Larga Distancia
// ============================================================================

/**
 * Sección promocional para servicios de larga distancia
 * Incluye imagen y lista de beneficios
 */
const LongDistanceSection = () => {
  return (
    <section className="py-16 md:py-32 bg-white" data-testid="long-distance-section">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Imagen del taxi real */}
          <div className="order-2 md:order-1">
            <img 
              src={TAXI_IMAGE_URL}
              alt="Taxi oficial Madrid Norte - Servicio larga distancia"
              className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
              loading="lazy"
            />
            {/* Badge del vehículo - Simplificado */}
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 bg-[#F5D109]/20 text-[#1A1A1A] text-xs font-semibold px-3 py-1 rounded-full">
                <ShieldCheck className="w-3 h-3" /> Vehículo Oficial
              </span>
              <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                <Leaf className="w-3 h-3" /> Etiqueta ECO
              </span>
              <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                <Leaf className="w-3 h-3" /> Etiqueta 0
              </span>
              <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 text-xs font-semibold px-3 py-1 rounded-full">
                Hasta 4 Pasajeros
              </span>
            </div>
          </div>
          
          {/* Contenido */}
          <div className="order-1 md:order-2">
            <div className="inline-flex items-center gap-2 bg-[#F5D109]/10 border border-[#F5D109]/30 px-4 py-2 rounded-full mb-6">
              <Route className="w-5 h-5 text-[#F5D109]" />
              <span className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-wide">Larga Distancia</span>
            </div>
            
            <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#1A1A1A] mb-4">
              Servicio de Larga Distancia / Otras Ciudades
            </h2>
            
            <p className="text-lg text-[#666666] mb-6 leading-relaxed">
              ¿Necesitas viajar fuera de Madrid? Ofrecemos servicio de taxi de larga distancia a cualquier 
              punto de España. Precios cerrados y sin sorpresas.
            </p>
            
            {/* Lista de beneficios */}
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-[#1A1A1A]">
                <BadgeCheck className="w-5 h-5 text-[#F5D109] flex-shrink-0" />
                <span>Vehículos cómodos para viajes largos</span>
              </li>
              <li className="flex items-center gap-3 text-[#1A1A1A]">
                <BadgeCheck className="w-5 h-5 text-[#F5D109] flex-shrink-0" />
                <span>Conductor profesional y experimentado</span>
              </li>
              <li className="flex items-center gap-3 text-[#1A1A1A]">
                <BadgeCheck className="w-5 h-5 text-[#F5D109] flex-shrink-0" />
                <span>Presupuesto sin compromiso</span>
              </li>
            </ul>
            
            <a 
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hola, necesito un taxi de larga distancia. ¿Podrías darme presupuesto?")}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              onClick={() => trackWhatsAppClick('long_distance', 'presupuesto')}
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white font-bold py-4 px-8 rounded-full text-lg hover:bg-[#F5D109] hover:text-black transition-colors"
              data-testid="long-distance-btn"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Pedir Presupuesto
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// COMPONENTE: Sección de Confianza
// ============================================================================

/**
 * Sección que muestra elementos de confianza y credibilidad
 * Licencia Oficial, Precios Oficiales, Flota ECO
 */
const TrustSection = () => {
  return (
    <section className="py-20 md:py-32 bg-[#F9F9F9]" data-testid="trust-section">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#1A1A1A]">
            Taxi de Confianza en Madrid Norte
          </h2>
        </div>

        {/* Tarjetas de confianza */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Licencia Oficial */}
          <Card className="bg-white border border-black/5 p-8 rounded-xl text-center card-hover" data-testid="trust-card-license">
            <CardContent className="p-0">
              <div className="w-16 h-16 bg-[#F5D109] rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-black" />
              </div>
              <h3 className="font-heading text-xl font-bold uppercase text-[#1A1A1A] mb-2">Licencia Oficial</h3>
              <p className="text-[#666666] text-sm">
                Taxi con licencia oficial del Ayuntamiento de Madrid. Vehículo asegurado y con todas las garantías legales.
              </p>
            </CardContent>
          </Card>

          {/* Precios Oficiales */}
          <Card className="bg-white border border-black/5 p-8 rounded-xl text-center card-hover" data-testid="trust-card-taximeter">
            <CardContent className="p-0">
              <div className="w-16 h-16 bg-[#F5D109] rounded-full flex items-center justify-center mx-auto mb-4">
                <Euro className="w-8 h-8 text-black" />
              </div>
              <h3 className="font-heading text-xl font-bold uppercase text-[#1A1A1A] mb-2">Precios Oficiales</h3>
              <p className="text-[#666666] text-sm">
                Taxímetro homologado por la Comunidad de Madrid. Tarifas oficiales sin sorpresas ni sobrecargos.
              </p>
            </CardContent>
          </Card>

          {/* Flota ECO */}
          <Card className="bg-white border border-black/5 p-8 rounded-xl text-center card-hover" data-testid="trust-card-eco">
            <CardContent className="p-0">
              <div className="w-16 h-16 bg-[#F5D109] rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-black" />
              </div>
              <h3 className="font-heading text-xl font-bold uppercase text-[#1A1A1A] mb-2">Flota ECO</h3>
              <p className="text-[#666666] text-sm">
                Vehículos ECO y CERO EMISIONES. Comprometidos con el medio ambiente y la movilidad sostenible.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// COMPONENTE: Sección de Reserva Móvil
// ============================================================================

/**
 * Sección de formulario de reserva solo visible en móvil
 * En desktop el formulario aparece en el Hero
 */
const MobileBookingSection = () => {
  return (
    <section id="reserva" className="py-12 md:py-24 bg-[#F9F9F9] md:hidden" data-testid="mobile-booking-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6">
          {/* H3 con keywords SEO */}
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#1A1A1A]">
            Reserva tu Taxi
          </h2>
        </div>
        <BookingForm />
      </div>
    </section>
  );
};

// ============================================================================
// COMPONENTE: Footer
// ============================================================================

/**
 * Pie de página con información de contacto y enlaces legales
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1A1A1A] text-white py-12 md:py-16" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
          {/* Columna: Marca y Contacto */}
          <div className="md:col-span-2">
            <h4 className="font-heading text-2xl font-black uppercase tracking-tighter mb-4">
              TuTaxi<span className="text-[#F5D109]">Madrid</span>Norte
            </h4>
            <p className="text-white/60 mb-4 max-w-sm">
              Servicio de taxi premium en Mirasierra, Barrio del Pilar y Fuencarral. 
              Jose Antonio Villacorta - Vehículos ECO y CERO EMISIONES.
            </p>
            {/* Botones de contacto */}
            <div className="flex gap-3">
              <a 
                href={`tel:+34${PHONE_NUMBER}`}
                rel="nofollow"
                onClick={() => trackCallClick('footer')}
                className="inline-flex items-center gap-2 bg-[#F5D109] text-black font-bold px-5 py-2 rounded-full text-sm hover:bg-[#E5C200] transition-colors"
                data-testid="footer-call-btn"
              >
                <Phone className="w-4 h-4" />
                639 759 160
              </a>
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_DEFAULT_MESSAGE}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={() => trackWhatsAppClick('footer')}
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-2 rounded-full text-sm hover:bg-[#20BD5A] transition-colors"
                data-testid="footer-whatsapp-btn"
              >
                <WhatsAppIcon className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </div>

          {/* Columna: Zonas de Servicio */}
          <div>
            <h5 className="font-semibold text-white mb-4 uppercase text-sm tracking-wide">Zonas de Servicio</h5>
            <ul className="space-y-2 text-white/60 text-sm">
              <li>Mirasierra</li>
              <li>Barrio del Pilar</li>
              <li>Fuencarral</li>
              <li>Madrid Norte</li>
              <li>Aeropuerto T1-T4</li>
            </ul>
          </div>

          {/* Columna: Enlaces Legales */}
          <div>
            <h5 className="font-semibold text-white mb-4 uppercase text-sm tracking-wide">Legal</h5>
            <ul className="space-y-2 text-white/60 text-sm">
              <li>
                <Link to="/aviso-legal" className="hover:text-[#F5D109] transition-colors" data-testid="legal-link">
                  Aviso Legal
                </Link>
              </li>
              <li>
                <Link to="/privacidad" className="hover:text-[#F5D109] transition-colors" data-testid="privacy-link">
                  Política de Privacidad
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="hover:text-[#F5D109] transition-colors" data-testid="cookies-link">
                  Política de Cookies
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Separador */}
        <div className="section-divider my-8"></div>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white/40 text-sm">
          <p>© {currentYear} TuTaxi Madrid Norte - Jose Antonio Villacorta. Todos los derechos reservados.</p>
          <p>Licencia Oficial de Taxi del Ayuntamiento de Madrid</p>
        </div>
      </div>
    </footer>
  );
};

// ============================================================================
// COMPONENTE: Botón Flotante de WhatsApp
// ============================================================================

/**
 * Botón flotante de WhatsApp fijo en la esquina inferior derecha
 * Con efecto de pulso para llamar la atención
 */
const FloatingWhatsApp = () => {
  return (
    <a 
      href={`https://wa.me/${WHATSAPP_NUMBER}`}
      target="_blank"
      rel="nofollow noopener noreferrer"
      onClick={() => trackWhatsAppClick('floating_button')}
      className="fixed bottom-6 right-4 md:right-6 z-40 w-16 h-16 md:w-14 md:h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform whatsapp-pulse"
      aria-label="Contactar por WhatsApp"
      data-testid="floating-whatsapp-btn"
    >
      <WhatsAppIcon className="w-8 h-8 md:w-7 md:h-7 text-white" />
    </a>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: App
// ============================================================================

/**
 * Componente principal de la aplicación
 * Ensambla todas las secciones de la landing page
 */
function App() {
  return (
    <div className="App min-h-screen bg-white">
      {/* Sistema de notificaciones toast */}
      <Toaster position="top-center" richColors />
      
      {/* Header con navegación */}
      <Header />
      
      {/* Contenido principal */}
      <main>
        {/* Sección Hero con formulario de reserva */}
        <HeroSection />
        
        {/* Sección de Servicios */}
        <ServicesSection />
        
        {/* Sección de Zonas de Cobertura (SEO) */}
        <ZonesSection />
        
        {/* Banner de Tarifa Aeropuerto */}
        <AirportSection />
        
        {/* Sección de Larga Distancia */}
        <LongDistanceSection />
        
        {/* Sección de Confianza */}
        <TrustSection />
        
        {/* Formulario de Reserva (solo móvil) */}
        <MobileBookingSection />
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* Botón flotante de WhatsApp */}
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
